// src/Components/Resident/ProfileTabs.jsx
import React, { useState } from "react";
import {
  Card, Form, Row, Col, Button, InputGroup,
  Alert, Table, Spinner, Tabs, Tab
} from "react-bootstrap";
import { useResidentProfile } from "../../hooks/useResidentProfile";

export default function ProfileSection() {
  const {
    loading, saving, error,
    mobile, setMobile,
    address, setAddress,
    family,
    saveContactAddress,
    addFamilyMember,
    removeFamilyMember,
    changePassword,
    computeAgeFromDOB,
  } = useResidentProfile();

  const [activeKey, setActiveKey] = useState("contact");
  const [msg, setMsg] = useState({ type: "", text: "" });

  // Family inputs (added dob & age)
  const [famName, setFamName] = useState("");
  const [famRel, setFamRel] = useState("");
  const [famDOB, setFamDOB] = useState("");  // YYYY-MM-DD
  const [famAge, setFamAge] = useState("");  // optional manual age

  // Password inputs
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");

  const handleSaveContact = async () => {
    setMsg({ type: "", text: "" });
    await saveContactAddress();
    setMsg({ type: "success", text: "Contact & address saved." });
  };

  const handleAddFamily = async () => {
    setMsg({ type: "", text: "" });
    await addFamilyMember(famName, famRel, famDOB, famAge);
    setFamName(""); setFamRel(""); setFamDOB(""); setFamAge("");
    setMsg({ type: "success", text: "Family member added." });
  };

  const handleChangePassword = async () => {
    setMsg({ type: "", text: "" });
    try {
      if (pwdNew !== pwdConfirm) {
        setMsg({ type: "warning", text: "New password and confirmation do not match." });
        return;
      }
      await changePassword(pwdCurrent, pwdNew);
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
      setMsg({ type: "success", text: "Password updated successfully." });
    } catch (err) {
      setMsg({ type: "danger", text: err?.message || "Failed to update password." });
    }
  };

  // Show computed age preview when DOB is provided
  const dobPreviewAge = computeAgeFromDOB(famDOB);

  return (
    <>
      {(error || msg.text) && (
        <Alert variant={error ? "danger" : msg.type} className="mb-3">
          {error || msg.text}
        </Alert>
      )}

      {loading ? (
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading profile…</span>
        </div>
      ) : (
        <Tabs activeKey={activeKey} onSelect={(k) => setActiveKey(k || "contact")} className="mb-3">
          {/* Contact & Address */}
          <Tab eventKey="contact" title="Contact & Address">
            <Card>
              <Card.Body>
                <Card.Title>Contact</Card.Title>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Mobile Number</Form.Label>
                      <InputGroup>
                        <InputGroup.Text><i className="bi bi-phone"></i></InputGroup.Text>
                        <Form.Control
                          value={mobile}
                          onChange={(e) => setMobile(e.target.value)}
                          placeholder="e.g., +91 98765 43210"
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        This saves in your profile document; phone auth/verification is separate.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Card.Title className="mt-4">Address</Card.Title>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Address Line 1</Form.Label>
                      <Form.Control
                        value={address.line1}
                        onChange={(e) => setAddress((a) => ({ ...a, line1: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Address Line 2</Form.Label>
                      <Form.Control
                        value={address.line2}
                        onChange={(e) => setAddress((a) => ({ ...a, line2: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        value={address.city}
                        onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        value={address.state}
                        onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Zip / PIN</Form.Label>
                      <Form.Control
                        value={address.zip}
                        onChange={(e) => setAddress((a) => ({ ...a, zip: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-3 d-flex gap-2">
                  <Button variant="primary" onClick={handleSaveContact} disabled={saving}>
                    {saving ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                    Save
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Tab>

          {/* Family */}
          <Tab eventKey="family" title="Family">
            <Card>
              <Card.Body>
                <Card.Title>Family Details</Card.Title>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        value={famName}
                        onChange={(e) => setFamName(e.target.value)}
                        placeholder="e.g., Ananya Sharma"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Relationship</Form.Label>
                      <Form.Select
                        value={famRel}
                        onChange={(e) => setFamRel(e.target.value)}
                      >
                        <option value="">Select…</option>
                        <option>Spouse</option>
                        <option>Father</option>
                        <option>Mother</option>
                        <option>Son</option>
                        <option>Daughter</option>
                        <option>Sibling</option>
                        <option>Relative</option>
                        <option>Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* New: DOB & Age */}
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Date of Birth (DOB)</Form.Label>
                      <Form.Control
                        type="date"
                        value={famDOB}
                        onChange={(e) => setFamDOB(e.target.value)}
                      />
                      {famDOB && (
                        <Form.Text className="text-muted">
                          Computed Age: <strong>{computeAgeFromDOB(famDOB) ?? "—"}</strong>
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Age (optional)</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        placeholder="If DOB not known"
                        value={famAge}
                        onChange={(e) => setFamAge(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="outline-primary"
                      className="w-100"
                      onClick={handleAddFamily}
                      disabled={saving}
                    >
                      Add
                    </Button>
                  </Col>
                </Row>

                <Table bordered hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th style={{ width: "30%" }}>Name</th>
                      <th style={{ width: "20%" }}>Relationship</th>
                      <th style={{ width: "20%" }}>DOB</th>
                      <th style={{ width: "15%" }}>Age</th>
                      <th style={{ width: "15%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {family?.length ? (
                      family.map((m) => {
                        const computed = m.dob ? computeAgeFromDOB(m.dob) : undefined;
                        const displayAge = computed ?? m.age ?? "—";
                        return (
                          <tr key={m.id}>
                            <td>{m.name}</td>
                            <td>{m.relationship}</td>
                            <td>{m.dob || "—"}</td>
                            <td>{displayAge}</td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => removeFamilyMember(m.id)}
                                disabled={saving}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          No family members added yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* Password */}
          <Tab eventKey="password" title="Password">
            <Card>
              <Card.Body>
                <Card.Title>Change Password</Card.Title>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Current Password</Form.Label>
                      <Form.Control
                        type="password"
                        autoComplete="current-password"
                        value={pwdCurrent}
                        onChange={(e) => setPwdCurrent(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        autoComplete="new-password"
                        value={pwdNew}
                        onChange={(e) => setPwdNew(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        autoComplete="new-password"
                        value={pwdConfirm}
                        onChange={(e) => setPwdConfirm(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-3 d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => { setPwdCurrent(""); setPwdNew(""); setPwdConfirm(""); }}
                  >
                    Reset
                  </Button>
                  <Button variant="primary" onClick={handleChangePassword} disabled={saving}>
                    {saving ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                    Update Password
                  </Button>
                </div>
                <Form.Text className="text-muted d-block mt-2">
                  You’ll be re-authenticated with your current password before updating.
                </Form.Text>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      )}
    </>
  );
}

